import { useId, useState, type FormEvent } from 'react';

import { Send } from 'lucide-react';

import { ApiError } from '@/api/client';
import type { CandidateRead, JobShare, Vacancy } from '@/api/schema';
import { Alert } from '@/components/feedback/Alert';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { ErrorState } from '@/components/ui/EmptyState';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useDebounced } from '@/lib/useDebounced';

import {
  useCareersOptions,
  useSendJobProfiles,
  useVacancies,
  type VacancyFilters,
} from '../api';
import { STATE_LABEL } from '../labels';
import styles from './JobProfilesPanel.module.css';

export function SendJobProfiles({
  candidate,
  onClose,
}: {
  candidate: Pick<CandidateRead, 'id' | 'full_name' | 'email'>;
  onClose: () => void;
}) {
  const formId = useId();
  const [introduction, setIntroduction] = useState(
    'Hello {candidate_name},\n\nHere are the opportunities we discussed.',
  );
  const [recipient, setRecipient] = useState(candidate.email ?? '');
  const [cc, setCc] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, Vacancy>>({});
  const [requestId] = useState(() => crypto.randomUUID());
  const [locked, setLocked] = useState(false);
  const [result, setResult] = useState<JobShare | null>(null);
  const [filters, setFilters] = useState<VacancyFilters>({
    page: 1,
    search: '',
    industry: '',
    country: '',
    state: '',
    available: true,
  });
  const search = useDebounced(filters.search, 250);
  const jobs = useVacancies({ ...filters, search });
  const options = useCareersOptions();
  const ccValue = cc ?? options.data?.cc?.join(', ') ?? '';
  const send = useSendJobProfiles(candidate.id);
  const count = Object.keys(selected).length;
  const filter = (values: Partial<VacancyFilters>) => {
    setFilters((current) => ({ ...current, ...values, page: 1 }));
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!count || send.isPending) return;
    setCc(ccValue);
    setLocked(true);
    try {
      setResult(
        await send.mutateAsync({
          request_id: requestId,
          recipient,
          cc: ccValue
            .split(',')
            .map((address) => address.trim())
            .filter(Boolean),
          introduction,
          vacancy_ids: Object.keys(selected),
        }),
      );
    } catch (error) {
      // These refusals occur before delivery is attempted. A network/server failure
      // might have sent the message: retain the payload and id when checking again.
      if (error instanceof ApiError && [400, 401, 403, 404, 422].includes(error.status))
        setLocked(false);
    }
  };
  const close = () => {
    if (!send.isPending) onClose();
  };
  return (
    <Modal
      open
      size="lg"
      title={`Send job profiles to ${candidate.full_name}`}
      onClose={close}
      description="Select up to 20 available vacancies. They will be included together in one email."
      footer={
        <>
          <Button variant="ghost" disabled={send.isPending} onClick={close}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result || result.state === 'pending' ? (
            <Button
              type="submit"
              form={formId}
              loading={send.isPending}
              disabled={send.isPending || !count || !options.data?.email_enabled}
              icon={<Send size={15} />}
            >
              {locked
                ? 'Check send result'
                : `Send ${count || ''} job ${count === 1 ? 'profile' : 'profiles'}`}
            </Button>
          ) : null}
        </>
      }
    >
      {options.isError ? (
        <ErrorState
          message="Company email settings could not be loaded."
          onRetry={() => {
            void options.refetch();
          }}
        />
      ) : options.data && !options.data.email_enabled ? (
        <Alert tone="warning">
          Company email is not configured yet. An administrator needs to connect the company
          mail service before profiles can be sent.
        </Alert>
      ) : null}
      {send.isError ? (
        <Alert tone="danger">
          {send.error instanceof ApiError
            ? send.error.message
            : 'The send result could not be confirmed.'}
          {locked ? ' Use Check send result before starting another email.' : ''}
        </Alert>
      ) : null}
      {result ? (
        <Alert
          tone={
            result.state === 'sent'
              ? 'success'
              : result.state === 'failed'
                ? 'danger'
                : 'warning'
          }
        >
          <strong>{STATE_LABEL[result.state]}.</strong> {result.detail}
        </Alert>
      ) : null}
      <form
        id={formId}
        onSubmit={(event) => {
          void submit(event);
        }}
        className={styles.form}
      >
        <Field
          label="Candidate email"
          required
          hint={options.data?.sender ? `From ${options.data.sender}` : undefined}
        >
          {(ids) => (
            <Input
              {...ids}
              type="email"
              required
              maxLength={320}
              value={recipient}
              disabled={locked}
              onChange={(event) => {
                setRecipient(event.target.value);
              }}
            />
          )}
        </Field>
        <p className={styles.hint}>
          You can enter a different email for this message. The candidate’s saved contact
          details will stay the same.
        </p>
        <Field
          label="CC recipients"
          hint="Starts with the existing company CC list. Separate addresses with commas, or clear the field to send without CCs. Changes apply to this email."
        >
          {(ids) => (
            <Input
              {...ids}
              type="email"
              multiple
              value={ccValue}
              disabled={locked}
              placeholder="name@example.com, another@example.com"
              onChange={(event) => {
                setCc(event.target.value);
              }}
            />
          )}
        </Field>
        <Field
          label="Message"
          hint="Use {candidate_name} and optionally {screening_date}. The email includes styled job details, application links and a Zoom booking footer."
        >
          {({ id }) => (
            <textarea
              id={id}
              rows={5}
              maxLength={4000}
              value={introduction}
              disabled={locked}
              onChange={(event) => {
                setIntroduction(event.target.value);
              }}
            />
          )}
        </Field>
        <div className={styles.filters}>
          <Input
            type="search"
            aria-label="Search available vacancies"
            placeholder="Search available vacancies"
            value={filters.search}
            onChange={(event) => {
              filter({ search: event.target.value });
            }}
          />
          <Select
            aria-label="Job industry"
            placeholder="All industries"
            value={filters.industry}
            options={(options.data?.industries ?? []).map((value) => ({ value, label: value }))}
            onChange={(event) => {
              filter({ industry: event.target.value });
            }}
          />
          <Select
            aria-label="Job country"
            placeholder="All countries"
            value={filters.country}
            options={(options.data?.countries ?? []).map((value) => ({ value, label: value }))}
            onChange={(event) => {
              filter({ country: event.target.value });
            }}
          />
        </div>
        <p role="status">
          {count} of 20 vacancies selected
          {count
            ? `: ${Object.values(selected)
                .map((job) => job.title)
                .join(', ')}`
            : ''}
        </p>
        {count > 0 ? (
          <div className={styles.selected}>
            {Object.values(selected).map((job) => (
              <Button
                key={job.id}
                size="sm"
                variant="subtle"
                disabled={locked}
                aria-label={`Remove ${job.title}`}
                onClick={() => {
                  setSelected((current) =>
                    Object.fromEntries(Object.entries(current).filter(([id]) => id !== job.id)),
                  );
                }}
              >
                {job.title} ×
              </Button>
            ))}
          </div>
        ) : null}
        {jobs.isPending ? (
          <Spinner label="Loading available vacancies" />
        ) : jobs.isError ? (
          <ErrorState
            message="The available vacancies did not load."
            onRetry={() => {
              void jobs.refetch();
            }}
          />
        ) : (
          <>
            {jobs.data.total === 0 ? (
              <p>No open published vacancies match these filters.</p>
            ) : (
              <ul className={styles.jobs}>
                {jobs.data.items.map((job) => (
                  <li key={job.id}>
                    <Checkbox
                      label={`${job.title} · ${job.country}`}
                      checked={Boolean(selected[job.id])}
                      disabled={locked || (count >= 20 && !selected[job.id])}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setSelected((current) => {
                          const next = { ...current };
                          if (checked) next[job.id] = job;
                          else
                            return Object.fromEntries(
                              Object.entries(current).filter(([id]) => id !== job.id),
                            );
                          return next;
                        });
                      }}
                    />
                    <p className={styles.hint}>
                      {[job.industry, job.salary, job.employment_type]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                    <details>
                      <summary>View job description</summary>
                      <p className={styles.description}>{job.description}</p>
                      {job.requirements ? (
                        <p className={styles.description}>{job.requirements}</p>
                      ) : null}
                    </details>
                  </li>
                ))}
              </ul>
            )}
            <Pagination
              page={filters.page}
              perPage={12}
              total={jobs.data.total}
              label="available vacancies"
              onChange={(page) => {
                setFilters((current) => ({ ...current, page }));
              }}
            />
          </>
        )}
      </form>
    </Modal>
  );
}
